import React from 'react';
import { Autocomplete, Chip, CircularProgress, Popper, TextField } from '@mui/material';
import useGetAllVendors from '../../hooks/vendors/useGetAllVendors';
import { Vendor } from '../../../types/vendor-types';
import DialogGlynt, { FormControlStyled, FormLabelStyled } from './DialogGlynt';
import DialogTitleGlynt from './DialogTitleGlynt';
import DialogContentGlynt from './DialogContentGlynt';
import DialogActionsGlynt from './DialogActionsGlynt';

interface ISetVendorsDialogProps {
  open: boolean;
  currentVendorIds?: number[];
  isUpdating: boolean;
  onClose: () => void;
  onSubmit: (vendorIds: number[]) => void;
}

const SetVendorsDialog: React.FC<ISetVendorsDialogProps> = ({
  open,
  currentVendorIds,
  isUpdating,
  onClose,
  onSubmit,
}) => {
  const [selectedVendorIds, setSelectedVendorIds] = React.useState<number[]>(
    currentVendorIds ?? []
  );

  const { data: vendors, isLoading } = useGetAllVendors();

  // Reset selected vendors when dialog opens
  React.useEffect(() => {
    if (open) {
      setSelectedVendorIds(currentVendorIds ?? []);
    }
  }, [open, currentVendorIds]);

  const handleSubmit = () => {
    onSubmit(selectedVendorIds);
  };

  const selectedVendors = React.useMemo(() => {
    if (!vendors) return [];
    return selectedVendorIds
      .map((id) => vendors.find((v) => v.id === id))
      .filter((v): v is Vendor => v !== undefined);
  }, [vendors, selectedVendorIds]);

  return (
    <DialogGlynt
      open={open}
      closeModal={onClose}
      width="500px"
      cypressId="dialog-set-vendors"
    >
      <DialogTitleGlynt title="Set Vendors" closeModal={onClose} />
      <DialogContentGlynt>
        <FormControlStyled fullWidth>
          <FormLabelStyled>VENDORS</FormLabelStyled>
          <Autocomplete
            multiple
            fullWidth
            options={vendors || []}
            value={selectedVendors}
            onChange={(event, newValue: Vendor[]) => {
              setSelectedVendorIds(newValue.map((v) => v.id));
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.name}
            disabled={isLoading}
            PopperComponent={(props) => (
              <Popper data-cy="popper-select-vendors-popup" {...props} placement="bottom-start" />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.name}
                  size="small"
                  {...getTagProps({ index })}
                  key={option.id}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder={selectedVendorIds.length === 0 ? 'Search vendors' : ''}
                data-cy="select-vendors"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </FormControlStyled>
      </DialogContentGlynt>
      <DialogActionsGlynt
        handleSubmit={handleSubmit}
        closeModal={onClose}
        cancelCypressId="btn-set-vendors-cancel"
        submitCypressId="btn-set-vendors-submit"
        clearCypressId="btn-set-vendors-clear"
        submitButtonText="Submit"
        submitLoading={isUpdating}
        showCancelButton={true}
        showClearButton={false}
        submitDisabled={isUpdating}
      />
    </DialogGlynt>
  );
};

export default SetVendorsDialog;
